import { useState } from "react";
import { VerdictControl, type StudyReviewModel } from "@/components/devicePanel/Review";
import type { DevicePanelStudyDevice } from "@/types/devicePanel";
import { REVIEW_TAGS, roundOf, sameTarget, type ReviewTarget } from "@/types/devicePanelReview";

/** A reviewer's feedback on one finding card: whether the finding is on the
 * images, whether it should notify a radiologist, and whether its key image
 * shows it. */
export function FindingFeedback({
  review,
  target,
}: {
  review: StudyReviewModel;
  target: ReviewTarget;
}) {
  return (
    <div data-finding-feedback className="flex flex-col gap-1 pt-1">
      {(["blind", "notify", "key_image"] as const).map((round) => (
        <VerdictControl key={round} review={review} round={round} target={target} label={null} />
      ))}
    </div>
  );
}

/**
 * Findings the devices claim but did not flag, for a reviewer to judge on the
 * images: the queued possible misses, those already judged, and any the
 * reviewer adds from the devices' claimed findings. Names only; no scores.
 */
export function MissedFindings({
  review,
  devices,
}: {
  review: StudyReviewModel;
  devices: DevicePanelStudyDevice[];
}) {
  const [added, setAdded] = useState<ReviewTarget[]>([]);
  const deviceLabels = new Map(devices.map((device) => [device.key, device.label]));
  // A finding whose device the study does not list has nothing to name it by; skip it.
  const missable = review.data.missable.filter((finding) => deviceLabels.has(finding.device));
  const { items, verdicts } = review.data;
  const queued = items.filter((item) => roundOf(item.kind) === "missed");
  const listed = [...queued, ...verdicts.filter((verdict) => verdict.round === "missed"), ...added];
  const rows = missable.filter((finding) => listed.some((target) => sameTarget(target, finding)));
  const choices = missable.filter((finding) => !rows.includes(finding));

  return (
    <section aria-labelledby="missed-heading" className="grid gap-2 border-t border-border pt-4">
      <h2 id="missed-heading" className="text-sm font-medium">
        Missed by the devices?
      </h2>
      <p className="text-xs text-fg-muted">
        Findings a device covers but did not flag. Mark what you see on the images.
      </p>
      {rows.length > 0 && (
        <ul aria-label="Possible misses" className="grid gap-2">
          {rows.map((finding) => (
            <li
              key={`${finding.device}:${finding.condition}`}
              aria-label={finding.display_name}
              className="grid gap-1"
            >
              <span className="text-xs">
                {finding.display_name}
                <span className="ml-2 font-mono text-[10px] text-fg-dim">
                  {deviceLabels.get(finding.device)}
                </span>
                {queued.some((item) => sameTarget(item, finding)) && (
                  <span className="ml-1.5 rounded-xs border border-border-strong px-1 font-mono text-[9.5px] text-fg-muted">
                    {REVIEW_TAGS.possible_missed_finding}
                  </span>
                )}
              </span>
              <VerdictControl
                review={review}
                round="missed"
                target={{ device: finding.device, condition: finding.condition }}
                label={null}
              />
            </li>
          ))}
        </ul>
      )}
      {choices.length > 0 && (
        <select
          aria-label="Add a missed finding"
          value=""
          onChange={(event) => {
            const finding = choices[Number(event.target.value)];
            setAdded((current) => [...current, finding]);
          }}
          className="w-fit rounded-xs border border-border-strong bg-bg-2 px-2 py-1 text-xs text-fg"
        >
          <option value="" disabled>
            Add a finding a device missed…
          </option>
          {devices
            .filter((device) => choices.some((finding) => finding.device === device.key))
            .map((device) => (
              <optgroup key={device.key} label={device.label}>
                {choices.map((finding, index) =>
                  finding.device === device.key ? (
                    <option key={finding.condition} value={index}>
                      {finding.display_name}
                    </option>
                  ) : null,
                )}
              </optgroup>
            ))}
        </select>
      )}
    </section>
  );
}
